/**
 * Client for the per-brokerage broker-access endpoints.
 *
 * Talks to `/api/broker_deals/broker_agent_access/...` — the CRUD surface that
 * maps an external broker-agent's email to a single brokerage (`brokers.id`).
 * A `BD.BrokerAgent` with a mapping here sees only that brokerage's deals
 * (server-side row scoping); with no mapping they see zero deals (fail-closed).
 *
 * All routes are gated to BD.Admin (+ de/admin) on the server — this is an
 * admin-only surface.
 *
 * Endpoints:
 *   - GET    /broker_deals/broker_agent_access
 *   - POST   /broker_deals/broker_agent_access           (409 dup email, 400 bad FK)
 *   - PUT    /broker_deals/broker_agent_access/{email}    (404 if missing)
 *   - DELETE /broker_deals/broker_agent_access/{email}    (404 if missing)
 *
 * Each method takes `{ token, userOid, ... }` and returns the raw axios
 * response — callers destructure `response.data.data` per the standard de-hub
 * envelope ({ success, message, response_time_s, data, count? }).
 *
 * Mirrors the `dealsService.js` / `brokerService.js` default-export pattern so
 * the page can mock the module wholesale in tests.
 */
import dataService from "./dataservice";

const BASE = "/broker_deals/broker_agent_access";
const MY_ACCESS = "/broker_deals/my-access";

/**
 * GET /api/broker_deals/broker_agent_access
 *
 * Returns every email→brokerage mapping. Each row carries
 * `{ email, name, brokerage_id, active, company_name, created_by, created_at,
 * updated_by, updated_at }`. `name` is the agent's display name and is the
 * value the deal `agent` dropdown offers (brokerage-scoped). `active` is the
 * suspend flag: SUSPENDED (`active: false`) mappings ARE returned — the
 * maintenance screen needs them to offer reactivation — so anything building
 * the deal `agent` dropdown must drop them. Readable by any BD role; writes
 * remain admin-only.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 */
const listBrokerAccess = ({ token, userOid }) =>
  dataService.getEndpoint(BASE, token, userOid);

/**
 * POST /api/broker_deals/broker_agent_access
 *
 * Create a mapping. Body shape (extra='forbid' on the server):
 *   { email (req), brokerage_id (req), name (req), active (opt),
 *     tier (opt), aoi_ids (opt) }
 *
 * `active` defaults to true server-side — omit it to onboard an active agent.
 *
 * `name` is the agent's display name, surfaced by the deal `agent` dropdown.
 * For `tier: 'agent'` it is also the MATCH KEY — that user sees the deals
 * whose Agent is this name — so the server rejects a blank one on that tier.
 *
 * `tier` is the row-scope tier (de-hub migration 088): 'admin' (every deal in
 * the brokerage — the default when omitted, and what a pre-088 row means),
 * 'lead' (only the AOIs in `aoi_ids`), 'agent' (only deals naming them). See
 * utils/brokerTiers.js.
 *
 * `aoi_ids` are stored for any tier but only enforced for 'lead'; a 'lead'
 * with none is refused with 400 rather than silently scoped to nothing.
 *
 * Errors: 409 if the email already has a mapping; 400 on empty email, an
 * unknown `brokerage_id` FK, or an unconfigurable tier; 422 on unknown fields
 * or a tier outside the three.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {{ email: string, brokerage_id: number, name: string,
 *           active?: boolean, tier?: ('admin'|'lead'|'agent'),
 *           aoi_ids?: number[] }} args.body
 */
const createBrokerAccess = ({ token, userOid, body }) =>
  dataService.postEndpoint(BASE, token, userOid, body);

/**
 * PUT /api/broker_deals/broker_agent_access/{email}
 *
 * Reassign an existing email to a different brokerage, update its display
 * `name`, and/or suspend / reactivate it via `active`. Body
 * `{ brokerage_id (req), name?, active? }`. 404 if no mapping exists for the
 * email. The email is the natural key and is URL-encoded here defensively.
 *
 * `active: false` SUSPENDS the agent: the mapping row (brokerage, name, audit
 * trail) is kept, but the server scopes them to nothing — they see zero deals
 * and every write 403s — and the app drops them from the deal `agent`
 * dropdown. `active: true` reactivates. Omitting `active` leaves the current
 * state untouched, so a rename never silently un-suspends anyone.
 *
 * `tier` / `aoi_ids` (de-hub migration 088) are partial the same way: omit
 * either to leave it unchanged. `aoi_ids` REPLACES the stored assignment
 * rather than merging into it — pass [] to clear. The server validates the
 * resulting END state, so moving someone to 'lead' and giving them AOIs in
 * one request is fine, while moving them with no AOIs is a 400.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string} args.email
 * @param {{ brokerage_id?: number, name?: string, active?: boolean,
 *           tier?: ('admin'|'lead'|'agent'), aoi_ids?: number[] }} args.body
 */
const updateBrokerAccess = ({ token, userOid, email, body }) =>
  dataService.updateEndpoint(
    `${BASE}/${encodeURIComponent(email)}`,
    token,
    userOid,
    body
  );

/**
 * DELETE /api/broker_deals/broker_agent_access/{email}
 *
 * Remove a mapping. Returns `{ data: { deleted, email } }`. 404 if missing.
 *
 * The HARD offboard — it discards the agent's display name and audit trail.
 * Prefer `updateBrokerAccess` with `{ active: false }` to suspend an agent
 * whose access may come back.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string} args.email
 */
const deleteBrokerAccess = ({ token, userOid, email }) =>
  dataService.deleteEndpoint(
    `${BASE}/${encodeURIComponent(email)}`,
    token,
    userOid
  );

/**
 * GET /api/broker_deals/my-access
 *
 * The CALLER's own access state, readable by any BD role. Envelope `data` is
 * `{ scoped: boolean, mapped: boolean, brokerage_id: number|null }`.
 * `mapped === false` is the only reliable signal that an external broker
 * agent is not linked to a brokerage yet. An empty deals list is not: the
 * list is server-filtered, so a mapped broker can legitimately see zero rows.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 */
const getMyAccess = ({ token, userOid }) =>
  dataService.getEndpoint(MY_ACCESS, token, userOid);

/**
 * Drop every cached GET response (see `dataService.clearCache`).
 *
 * The shared axios instance caches GETs for 5 minutes and never invalidates on
 * a mutation, so the mapping list re-fetched straight after an add / rename /
 * suspend / remove would still be the pre-mutation copy — a suspended agent
 * would keep showing as ACTIVE until the TTL lapsed. Call this before
 * re-fetching.
 *
 * @returns {Promise<void>}
 */
const invalidateCache = () => dataService.clearCache();

const brokerAccessService = {
  listBrokerAccess,
  createBrokerAccess,
  updateBrokerAccess,
  deleteBrokerAccess,
  getMyAccess,
  invalidateCache,
};

export default brokerAccessService;
